import canadaLocationsJson from './canada_locations.json';

export interface Country {
    name: string;
    code: string;
    provinces: Province[];
}

export interface Province {
    name: string;
    code: string;
    cities: string[];
}

/** Single source: {@see ./canada_locations.json} — Laravel sitemap code reads the same file from disk. */
export const CANADA_DATA: Country = canadaLocationsJson as Country;

/**
 * Maps each Canadian province/territory code to its primary IANA timezone.
 * Saskatchewan has no DST; Newfoundland uses a non-standard -3:30 offset.
 */
export const PROVINCE_TIMEZONE_MAP: Record<string, string> = {
    AB: 'America/Edmonton',
    BC: 'America/Vancouver',
    MB: 'America/Winnipeg',
    NB: 'America/Moncton',
    NL: 'America/St_Johns',
    NT: 'America/Yellowknife',
    NS: 'America/Halifax',
    NU: 'America/Iqaluit',
    ON: 'America/Toronto',
    PE: 'America/Halifax',
    QC: 'America/Toronto',
    SK: 'America/Regina',
    YT: 'America/Whitehorse',
};

/**
 * Returns the IANA timezone for a province name or code.
 * Falls back to 'America/Toronto' when unknown.
 */
export const getTimezoneForProvince = (provinceNameOrCode: string): string => {
    // Try direct code lookup first
    if (PROVINCE_TIMEZONE_MAP[provinceNameOrCode.toUpperCase()]) {
        return PROVINCE_TIMEZONE_MAP[provinceNameOrCode.toUpperCase()];
    }
    // Try finding by name, then look up its code
    const province = CANADA_DATA.provinces.find(
        (p) => p.name === provinceNameOrCode || p.code === provinceNameOrCode.toUpperCase()
    );
    return province ? (PROVINCE_TIMEZONE_MAP[province.code] ?? 'America/Toronto') : 'America/Toronto';
};

// Helper functions
export const getProvinceByCode = (code: string): Province | undefined => {
    return CANADA_DATA.provinces.find((province) => province.code === code);
};

export const getProvinceByName = (name: string): Province | undefined => {
    return CANADA_DATA.provinces.find((province) => province.name === name);
};

export const getAllCities = (): string[] => {
    return CANADA_DATA.provinces.flatMap((province) => province.cities);
};

export const getCitiesByProvinceCode = (provinceCode: string): string[] => {
    const province = getProvinceByCode(provinceCode);
    return province ? province.cities : [];
};

export const getCitiesByProvinceName = (provinceName: string): string[] => {
    const province = getProvinceByName(provinceName);
    return province ? province.cities : [];
};

/** Two-letter code or full province/territory name → canonical name for LocationContext storage. */
export function resolveProvinceNameForUserLocation(input: string): string | null {
    const t = input.trim();
    if (!t) {
        return null;
    }
    if (t.length === 2) {
        const province = CANADA_DATA.provinces.find((p) => p.code.toLowerCase() === t.toLowerCase());
        return province?.name ?? null;
    }
    const byName = CANADA_DATA.provinces.find((p) => p.name.toLowerCase() === t.toLowerCase());
    return byName?.name ?? null;
}